import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, X, XCircle, Ban } from 'lucide-react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { reportExportsAPI } from '../services/api/reportExportsAPI';
import { triggerBlobDownload } from '../utils/downloadUtils';

export interface ReportExportRequest {
  date: string;
  doctorId: string;
  doctorName: string;
  physicianMatch?: string;
  sendEmail: boolean;
  email?: string;
}

type ExportPhase = 'running' | 'cancelling' | 'completed' | 'failed' | 'cancelled';
type EmailPhase = 'idle' | 'sending' | 'sent' | 'error';

interface ActiveExport {
  taskId: string;
  doctorName: string;
  date: string;
  sendEmail: boolean;
  email?: string | undefined;
  phase: ExportPhase;
  done: number;
  total: number;
  exported: number;
  error?: string | undefined;
  emailPhase: EmailPhase;
}

interface ReportExportContextType {
  activeExport: ActiveExport | null;
  startExport: (req: ReportExportRequest) => Promise<void>;
  cancelExport: () => Promise<void>;
  dismissExport: () => void;
  downloadAgain: () => Promise<void>;
}

const ReportExportContext = createContext<ReportExportContextType | undefined>(undefined);

const POLL_INTERVAL_MS = 1500;

export const ReportExportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeExport, setActiveExport] = useState<ActiveExport | null>(null);
  const pollRef = useRef<number | null>(null);
  const stateRef = useRef<ActiveExport | null>(null);
  stateRef.current = activeExport;

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const patch = useCallback((partial: Partial<ActiveExport>) => {
    setActiveExport((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  const downloadZip = useCallback(async (taskId: string, doctorName: string, date: string) => {
    const blob = await reportExportsAPI.downloadExportZip(taskId);
    const safeName = doctorName.replace(/\s+/g, '_');
    triggerBlobDownload(blob, `informes_${safeName}_${date}.zip`);
  }, []);

  const sendEmail = useCallback(
    async (taskId: string, email: string) => {
      patch({ emailPhase: 'sending' });
      try {
        await reportExportsAPI.sendExportEmail(taskId, { email_to: email });
        patch({ emailPhase: 'sent' });
      } catch {
        patch({ emailPhase: 'error' });
      }
    },
    [patch]
  );

  const poll = useCallback(
    (taskId: string) => {
      pollRef.current = window.setTimeout(async () => {
        const current = stateRef.current;
        if (!current || current.taskId !== taskId) return;
        try {
          const status = await reportExportsAPI.getExportStatus(taskId);
          const done = status.progress?.done ?? 0;
          const total = status.progress?.total ?? 0;
          if (status.status === 'completed') {
            stopPolling();
            const exported = status.summary?.exported ?? 0;
            patch({ phase: 'completed', done, total, exported });
            try {
              await downloadZip(taskId, current.doctorName, current.date);
            } catch {
              // Download can be retried from the toast
            }
            if (current.sendEmail && current.email) {
              sendEmail(taskId, current.email);
            }
            return;
          }
          if (status.status === 'failed') {
            stopPolling();
            patch({ phase: 'failed', done, total, error: status.error });
            return;
          }
          if (status.status === 'cancelled') {
            stopPolling();
            patch({ phase: 'cancelled', done, total });
            window.setTimeout(() => {
              setActiveExport((prev) => (prev?.phase === 'cancelled' ? null : prev));
            }, 4000);
            return;
          }
          patch({ done, total });
          poll(taskId);
        } catch {
          stopPolling();
          patch({ phase: 'failed', error: 'network' });
        }
      }, POLL_INTERVAL_MS);
    },
    [downloadZip, patch, sendEmail, stopPolling]
  );

  const startExport = useCallback(
    async (req: ReportExportRequest) => {
      stopPolling();
      const { task_id } = await reportExportsAPI.startDayExport({
        date: req.date,
        doctor_id: req.doctorId,
        ...(req.physicianMatch ? { physician_match: req.physicianMatch } : {}),
      });
      setActiveExport({
        taskId: task_id,
        doctorName: req.doctorName,
        date: req.date,
        sendEmail: req.sendEmail,
        email: req.email,
        phase: 'running',
        done: 0,
        total: 0,
        exported: 0,
        emailPhase: 'idle',
      });
      poll(task_id);
    },
    [poll, stopPolling]
  );

  const cancelExport = useCallback(async () => {
    const current = stateRef.current;
    if (!current || current.phase !== 'running') return;
    patch({ phase: 'cancelling' });
    try {
      await reportExportsAPI.cancelExport(current.taskId);
    } catch {
      // If cancel races with completion, polling resolves the final state
    }
  }, [patch]);

  const dismissExport = useCallback(() => {
    stopPolling();
    setActiveExport(null);
  }, [stopPolling]);

  const downloadAgain = useCallback(async () => {
    const current = stateRef.current;
    if (!current || current.phase !== 'completed') return;
    await downloadZip(current.taskId, current.doctorName, current.date);
  }, [downloadZip]);

  const value: ReportExportContextType = {
    activeExport,
    startExport,
    cancelExport,
    dismissExport,
    downloadAgain,
  };

  return (
    <ReportExportContext.Provider value={value}>
      {children}
      {activeExport && <ReportExportToast exportState={activeExport} />}
    </ReportExportContext.Provider>
  );
};

const ReportExportToast: React.FC<{ exportState: ActiveExport }> = ({ exportState }) => {
  const { t } = useTranslation();
  const { cancelExport, dismissExport, downloadAgain } = useReportExport();
  const { phase, done, total, exported, doctorName, emailPhase, email } = exportState;
  const running = phase === 'running' || phase === 'cancelling';
  const progressPct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="fixed bottom-4 right-4 z-[140] w-80 rounded-xl border border-brand-primary/30 bg-background-card shadow-gynhub-card">
      <div className="flex items-center justify-between gap-2 border-b border-border-light px-4 py-3">
        <span className="truncate font-futura text-sm text-text-primary">
          {t('reportExport.toastTitle', 'Informes del día')} — {doctorName}
        </span>
        {!running && (
          <button
            type="button"
            onClick={dismissExport}
            className="rounded-md p-1 text-text-secondary hover:text-brand-primary shrink-0"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="px-4 py-3 space-y-3">
        {running && (
          <>
            <div className="flex items-center gap-2 text-sm text-text-primary">
              <Loader2 className="h-4 w-4 animate-spin text-brand-primary shrink-0" />
              {phase === 'cancelling'
                ? t('reportExport.toastCancelling', 'Cancelando…')
                : total > 0
                  ? t('reportExport.progressGenerating', 'Generando PDFs… {{done}}/{{total}}', { done, total })
                  : t('reportExport.progressSearching', 'Buscando citas…')}
            </div>
            <div className="h-1.5 w-full rounded-full bg-border-light overflow-hidden">
              <div
                className="h-full rounded-full bg-brand-primary transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <button
              type="button"
              onClick={cancelExport}
              disabled={phase === 'cancelling'}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-border-light bg-background-input px-3 py-2 text-sm font-medium text-text-secondary hover:text-brand-primary disabled:opacity-50"
            >
              <Ban className="h-4 w-4" />
              {t('reportExport.toastCancel', 'Cancelar exportación')}
            </button>
          </>
        )}

        {phase === 'completed' && (
          <>
            <div className="flex items-center gap-2 text-sm text-text-primary">
              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
              {exported > 0
                ? t('reportExport.toastCompleted', '{{count}} informes listos. El ZIP se descargó.', { count: exported })
                : t('reportExport.noReportsTitle', 'Sin informes para exportar')}
            </div>
            {emailPhase === 'sending' && (
              <div className="flex items-center gap-2 text-xs text-text-secondary">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t('reportExport.sendingEmail', 'Enviando correo…')}
              </div>
            )}
            {emailPhase === 'sent' && (
              <div className="text-xs text-green-600">
                {t('reportExport.successEmailSentTo', 'ZIP enviado a {{email}}', { email })}
              </div>
            )}
            {emailPhase === 'error' && (
              <div className="text-xs text-red-500">
                {t('reportExport.errorEmailGeneric', 'El ZIP se descargó, pero no se pudo enviar el correo. Intente de nuevo.')}
              </div>
            )}
            {exported > 0 && (
              <button
                type="button"
                onClick={downloadAgain}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand-primary px-3 py-2 text-sm font-medium text-white hover:bg-brand-400"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                {t('reportExport.toastDownloadAgain', 'Descargar ZIP de nuevo')}
              </button>
            )}
          </>
        )}

        {phase === 'failed' && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <XCircle className="h-4 w-4 shrink-0" />
            {t('reportExport.errorGeneric', 'No se pudieron generar los informes. Intente de nuevo.')}
          </div>
        )}

        {phase === 'cancelled' && (
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <Ban className="h-4 w-4 shrink-0" />
            {t('reportExport.toastCancelled', 'Exportación cancelada')}
          </div>
        )}
      </div>
    </div>
  );
};

export const useReportExport = (): ReportExportContextType => {
  const context = useContext(ReportExportContext);
  if (!context) {
    throw new Error('useReportExport must be used within a ReportExportProvider');
  }
  return context;
};
